import { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import { Row, Col, Modal, Button, Form, Container } from "react-bootstrap"; // ✅ Import Container for consistent layout
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import axios from "axios";
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';


const BASE_URL =   process.env.REACT_APP_BASE_URL; // Adjust the base URL as needed

const localizer = momentLocalizer(moment);

// Validation schema for Doctor Availability
const doctorAvailabilityValidation = Yup.object().shape({
  doctorIds: Yup.array()
    .min(1, 'Please select at least one doctor')
    .required('Doctor selection is required'),
  start: Yup.string()
    .required('Start time is required'),
  end: Yup.string()
    .required('End time is required')
    .test('end-after-start', 'End time must be after start time', function (value) {
      const { start } = this.parent;
      if (!start || !value) return false;
      return moment(value).isAfter(moment(start));
    }),
  assignType: Yup.string()
    .required('Assign Type is required')
    .oneOf(['specific', 'filter'], 'Invalid assign type'),
  selectedMonth: Yup.string().when('assignType', (assignType, schema) =>
    assignType === 'filter' ? schema.required('Month is required when Filter is selected') : schema
  ),
  recurringDays: Yup.array().when('assignType', (assignType, schema) =>
    assignType === 'filter' ? schema.min(1, 'Please select at least one day') : schema
  ),
  recurringWeeks: Yup.array().when('assignType', (assignType, schema) =>
    assignType === 'filter' ? schema.min(1, 'Please select at least one week') : schema
  ),
});


const DoctorAvailability = () => {

const user = JSON.parse(localStorage.getItem('user'));

const [hospitalId, setHospitalId] = useState('');
const [subHospitalId, setSubHospitalId] = useState('');
  const loggedInHospitalName = user?.hospital_name || "Unknown Hospital";
  const [events, setEvents] = useState([]);
  const [currentView, setCurrentView] = useState(Views.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date()); // ✅ Track the current date
  const [showModal, setShowModal] = useState(false);
  const [subHospitals, setSubHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedSubHospital, setSelectedSubHospital] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(0);
  

  
  const [newEvent, setNewEvent] = useState({
    title: "",
    start: "",
    end: "",
    doctorIds: [], // ✅ Updated to handle multiple doctor IDs
    hospitalId: hospitalId, // ✅ Auto-fetch hospital ID
    subHospitalId: "", // ✅ Auto-populate based on selection
    recurringDays: [], // ✅ New field for recurring days
    recurringWeeks: [], // ✅ New field for recurring weeks
    isSpecificDate: true, // ✅ New field for specific date (default to Specific Date)
  });
  const [filterType, setFilterType] = useState(""); // ✅ Track filter type
  const [selectedMonth, setSelectedMonth] = useState(""); // ✅ Track selected month
  const [attemptedSubmit, setAttemptedSubmit] = useState(false); // track submit attempts to show errors

  useEffect(() => {
  const storedUser = JSON.parse(localStorage.getItem('user'));
  if (storedUser) {
    setHospitalId(storedUser.hospital_id );
    setSubHospitalId(storedUser.sub_hospital_id );
    setSelectedSubHospital(storedUser.sub_hospital_id );
  }
}, []);

useEffect(() => {
  const storedUser = JSON.parse(localStorage.getItem('user'));
  const hospitalId = storedUser?.hospital_id;

  if (!hospitalId) {
    console.error("Error: Hospital ID is not found in localStorage.");
    return;
  }

  axios
    .get(`${BASE_URL}/api/subHospitals/getSubHospitalsByHospital/${hospitalId}`)
    .then((response) => setSubHospitals(response.data))
    .catch((error) => console.error("Error fetching sub-hospitals:", error));
}, []);

useEffect(() => {
  const fetchDoctorsAndAvailability = async () => {
    try {
      const doctorsRes = await axios.get(`${BASE_URL}/api/doctorAvailability/getDoctorsByHospitalAndSubHospital`, {
        params: {
          hospitalId: hospitalId,
          subHospitalId: selectedSubHospital,
        },
      });
      setDoctors(doctorsRes.data);

      const response = await axios.get(`${BASE_URL}/api/doctorAvailability/getDoctorAvailability`, {
        params: {
          hospitalId: hospitalId,
          subHospitalId: selectedSubHospital,
        },
      });

      const filteredEvents = response.data.flatMap((event) => {
        const eventMonth = parseInt(event.recurring_monthly, 10);
        const currentMonth = currentDate.getMonth() + 1;

        if (eventMonth && eventMonth !== currentMonth) return [];

        const weeks = event.recurring_weeks?.split(",") || [];
        const days = event.recurring_days?.split(",") || [];
        const dayOfWeek = moment(event.start).format("dddd");
        const weekOfMonth = Math.ceil(new Date(event.start).getDate() / 7);

        if (weeks.length && !weeks.includes(weekOfMonth.toString())) return [];
        if (days.length && !days.includes(dayOfWeek)) return [];
       // console.log("Event object:", event);

        return {
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
          title: `${event.doctorNames}`,
        };
      });

      setEvents(filteredEvents);
    } catch (error) {
      console.error("Error in fetching doctors or availability:", error);
    }
  };

  if (hospitalId && selectedSubHospital) {
    fetchDoctorsAndAvailability(); // ✅ Only call if both IDs are available
  }
}, [selectedSubHospital, hospitalId, currentDate, refreshFlag]); 


  const handleSelectSlot = ({ start, end }) => {
    // Open the modal only for creating a new event
    setNewEvent({
      title: "",
      start,
      end,
      doctorIds: [],
      hospitalId: hospitalId,
      subHospitalId: selectedSubHospital,
      recurringDays: [],
      recurringWeeks: [],
      isSpecificDate: true,
    });
    setFilterType("");
    setSelectedMonth("");
    setAttemptedSubmit(false);
    setShowModal(true);
  };


 const handleDeleteEvent = (eventId) => {
  Swal.fire({
    title: 'Are you sure?',
    text: "You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, delete it!',
    didOpen: (modal) => {
      modal.parentElement.style.zIndex = '10000';
    },
  }).then((result) => {
    if (result.isConfirmed) {
      axios
        .delete(`${BASE_URL}/api/doctorAvailability/deleteDoctorAvailability/${eventId}`)
        .then(() => {
          setEvents(events.filter((event) => event.id !== eventId));
          setRefreshFlag(prev => prev + 1);
          setShowModal(false);

          // ✅ Show toast after successful deletion
          toast.success('Event deleted successfully!');
        })
        .catch((error) => {
          console.error("Error deleting event:", error);
          toast.error('Failed to delete event!');
        });
    }
  });
};


const handleSelectEvent = async (fetchedEvent) => {
  const storedUser = JSON.parse(localStorage.getItem('user'));
  const hospitalId = storedUser?.hospital_id;
    const subHospitalId =  storedUser?.sub_hospital_id;
  try {
    const doctorsRes = await axios.get(`${BASE_URL}/api/doctorAvailability/getDoctorsByHospitalAndSubHospital`, {
      params: {
        hospitalId,
        subHospitalId, 
      },
    });

    setDoctors(doctorsRes.data);
    console.log("Fetched doctors:", doctorsRes.data);
    // Set the new event state with fetched data
    setNewEvent({
  ...fetchedEvent,
  hospitalId: hospitalId,
  doctorIds: fetchedEvent.doctorIds?.map(Number) || [],
  doctor_name: fetchedEvent.doctor_name,
  subHospitalId: subHospitalId,
  start: new Date(fetchedEvent.start),
  end: new Date(fetchedEvent.end),
  recurringDays: Array.isArray(fetchedEvent.recurringDays)
    ? fetchedEvent.recurringDays
    : (fetchedEvent.recurringDays || "").split(","),
  recurringWeeks: Array.isArray(fetchedEvent.recurringWeeks)
    ? fetchedEvent.recurringWeeks
    : (fetchedEvent.recurringWeeks || "").split(","),
  isSpecificDate: !!fetchedEvent.isSpecificDate,
});

    setSelectedMonth(fetchedEvent.recurringMonthly?.toString() || "");
    setFilterType(fetchedEvent.isSpecificDate ? "" : "weeks");
    setShowModal(true);
    setAttemptedSubmit(false);
  } catch (err) {
    console.error("Error fetching doctor list:", err);
  }
};


// const handleSaveEvent = async () => {
//   // Validation checks before save
//   if (!newEvent.doctorIds || newEvent.doctorIds.length === 0) {
//     toast.error('Please select at least one doctor');
//     return;
//   }
//   if (!newEvent.start) {
//     toast.error('Please select start time');
//     return;
//   }
//   if (!newEvent.end) {
//     toast.error('Please select end time');
//     return;
//   }
//   if (moment(newEvent.end).isBefore(moment(newEvent.start))) {
//     toast.error('End time must be after start time');
//     return;
//   }

//   try {
//     const payload = {
//       ...newEvent,
//       recurringDays: newEvent.recurringDays.join(","),
//       recurringWeeks: newEvent.recurringWeeks.join(","),
//       recurringMonthly: selectedMonth,
//       hospitalId: hospitalId,
//       subHospitalId: selectedSubHospital,
//     };

//     if (newEvent.id) {
//       // Update
//       await axios.put(`${BASE_URL}/api/doctorAvailability/updateDoctorAvailability/${newEvent.id}`, payload);
//       toast.success('Availability updated successfully!');
//     } else {
//       // Create
//       await axios.post(`${BASE_URL}/api/doctorAvailability/createDoctorAvailability`, payload);
//       toast.success('Availability added successfully!');
//     }
//     setRefreshFlag(prev => prev + 1);
//     setShowModal(false);

    
//     // Force refresh calendar events
//     setSelectedSubHospital(selectedSubHospital);
//   } catch (error) {
//     const errorMsg =
//       error.response?.data?.message ||
//       'Failed to save availability. Please try again.';
//     toast.error(errorMsg);
//     console.error("Error saving doctor availability:", error);
//   }
// };

// New: Save using Formik values directly to avoid state sync issues
const handleSaveEventFromValues = async (values) => {
  // Additional validation to prevent submission if Filter is chosen without month/week/days
  if (values.assignType === 'filter') {
    if (!values.selectedMonth || values.selectedMonth === '') {
      toast.error('Month is required when Filter is selected');
      return;
    }
    if (!values.recurringWeeks || values.recurringWeeks.length === 0) {
      toast.error('Please select at least one week');
      return;
    }
    if (!values.recurringDays || values.recurringDays.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
  }

  // Check if doctors are selected
  if (!values.doctorIds || values.doctorIds.length === 0) {
    toast.error('Please select at least one doctor');
    return;
  }

  // Yup validation is already enforced by Formik; if we reach here, validation passed
  // Additional runtime checks only
  if (moment(values.end).isBefore(moment(values.start))) {
    toast.error('End time must be after start time');
    return;
  }

  try {
    const payload = {
      ...newEvent,
      ...values,
      recurringDays: (values.recurringDays || []).join(','),
      recurringWeeks: (values.recurringWeeks || []).join(','),
      recurringMonthly: values.selectedMonth || selectedMonth,
      hospitalId: hospitalId,
      subHospitalId: selectedSubHospital,
    };

    if (values.id) {
      await axios.put(`${BASE_URL}/api/doctorAvailability/updateDoctorAvailability/${values.id}`, payload);
      toast.success('Availability updated successfully!');
    } else {
      await axios.post(`${BASE_URL}/api/doctorAvailability/createDoctorAvailability`, payload);
      toast.success('Availability added successfully!');
    }

    setRefreshFlag(prev => prev + 1);
    setShowModal(false);
    setSelectedSubHospital(selectedSubHospital);
    setAttemptedSubmit(false);
  } catch (error) {
  let message = "Failed to save availability.";

  if (error.response) {
    // Backend ne message diya hai?
    if (error.response.data?.message) {
      message = error.response.data.message;
    } else if (typeof error.response.data === "string") {
      message = error.response.data;
    }
  } else if (error.message) {
    message = error.message;
  }

  toast.error(message);
  console.error("Error saving doctor availability:", error);
}
};

  return (
    <div className="d-flex flex-column flex-md-row">
      <Sidebar className="d-none d-md-block" />
      <Container fluid className="p-3">
        {/* Heading and Sub-Hospital Selection in same row for laptop/desktop */}
        <div className="my-3 pt-2 pb-4">
          <Container>
            <Row className="align-items-center flex-column flex-md-row">
              <Col xs={12} md={8}>
                <h2 className="global-heading mb-0 text-center text-md-start">
                  Manage Doctor Availability
                </h2>
                <div style={{
                  fontSize: '1.08rem',
                  color: '#444',
                  marginTop: 6,
                  fontWeight: 500
                }}>
                  Assign, view, and manage doctor schedules for each sub-hospital.
                </div>
              </Col>
              <Col xs={12} md={4} className="mt-3 mt-md-0 d-flex justify-content-md-end justify-content-start">
                <Form.Group style={{
                  minWidth: 240,
                  marginBottom: 0,
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(10,120,132,0.04)",
                  padding: "0.7rem 1rem"
                }}>
                  <Form.Label
                    className="fw-bold"
                    style={{
                      fontSize: "1.1rem",
                      color: "var(--primary-color)",
                      marginBottom: 8,
                      fontWeight: 600,
                      letterSpacing: 0.2
                    }}
                  >
                    Sub-Hospital
                  </Form.Label>
                  <Form.Control
                    as="select"
                    className="p-2 border-1 rounded"
                    style={{
                      fontSize: "1rem",
                      backgroundColor: "var(--input-bg-color)",
                      color: "var(--input-text-color)",
                      border: "1px solid #ced4da"
                    }}
                    value={selectedSubHospital || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setSelectedSubHospital(selectedId);
                      setNewEvent({ ...newEvent, subHospitalId: selectedId });
                      setEvents([]);
                      setDoctors([]);
                    }}
                  >
                    <option value="" disabled>
                      -- Select Sub-Hospital --
                    </option>
                    {subHospitals.map((subHospital) => (
                      <option key={subHospital.sub_hospital_id} value={subHospital.sub_hospital_id}>
                        {subHospital.sub_hospital_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </div>

        {/* Calendar */}
        <div className="calendar-container mt-4">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable
          style={{ height: "70vh" }}
          views={[Views.DAY, Views.WEEK, Views.MONTH]}
          view={currentView}
          date={currentDate}
          onView={(view) => setCurrentView(view)}
          onNavigate={(date) => setCurrentDate(date)}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          formats={{
            eventTimeRangeFormat: () => "", // 👈 Hides the time range in event box
          }}
        />

        </div>

        {/* Modal for Creating Availability */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          backdrop="static"
          className="form-modal"
          size="md"
        >
          <Modal.Header closeButton>
            <Modal.Title className="global-heading" style={{ fontSize: "1.2rem" }}>
              {newEvent.id ? "Edit Availability" : "Create Availability"}
            </Modal.Title>
          </Modal.Header>
          <Formik
            enableReinitialize
            initialValues={{
              id: newEvent.id || '',
              doctorIds: newEvent.doctorIds || [],
              assignType: newEvent.isSpecificDate ? 'specific' : 'filter',
              selectedMonth: selectedMonth || '',
              recurringWeeks: newEvent.recurringWeeks || [],
              recurringDays: newEvent.recurringDays || [],
              start: newEvent.start ? moment(newEvent.start).format("YYYY-MM-DDTHH:mm") : "",
              end: newEvent.end ? moment(newEvent.end).format("YYYY-MM-DDTHH:mm") : "",
            }}
            validationSchema={doctorAvailabilityValidation}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              await handleSaveEventFromValues(values);
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, isSubmitting, values, errors, touched, setFieldValue, submitCount }) => (
              <FormikForm onSubmit={handleSubmit}>
                <Modal.Body className="p-4">
                  <div>             
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                        Select Doctors *
                      </Form.Label>
                      <div>
                        {[...new Map(doctors.map((d) => [d.id, d])).values()]  // ✅ Remove duplicates based on ID
                          .map((doctor) => (
                            <Form.Check
                              key={`doctor-${doctor.id}`}
                              type="checkbox"
                              label={doctor.name}
                              value={doctor.id}
                              checked={values.doctorIds?.includes(doctor.id) || false}
                              onChange={(e) => {
                                const selectedDoctorId = parseInt(e.target.value, 10);
                                const updatedDoctorIds = e.target.checked
                                  ? [...(values.doctorIds || []), selectedDoctorId]
                                  : (values.doctorIds || []).filter((id) => id !== selectedDoctorId);
                                setFieldValue('doctorIds', updatedDoctorIds);
                                setNewEvent({ ...newEvent, doctorIds: updatedDoctorIds });
                              }}
                              className="mb-2"
                              style={{ color: "#495057" }}
                            />
                          ))}
                      </div>
                      {errors.doctorIds && (touched.doctorIds || submitCount > 0 || attemptedSubmit) && (
                        <div className="text-danger small mt-2">{errors.doctorIds}</div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                        Assign Type *
                      </Form.Label>
                      <div className="d-flex gap-3">
                        <Form.Check
                          type="radio"
                          label="Specific Date"
                          name="assignType"
                          checked={values.assignType === 'specific'}
                          onChange={() => {
                            setFieldValue('assignType', 'specific');
                            setFieldValue('recurringDays', []);
                            setFieldValue('recurringWeeks', []);
                            setNewEvent({ ...newEvent, isSpecificDate: true, recurringDays: [], recurringWeeks: [] });
                            setFilterType("");
                            setSelectedMonth("");
                          }}
                          className="form-check-inline"
                        />
                        <Form.Check
                          type="radio"
                          label="Filter"
                          name="assignType"
                          checked={values.assignType === 'filter'}
                          onChange={() => {
                            setFieldValue('assignType', 'filter');
                            setNewEvent({ ...newEvent, isSpecificDate: false });
                            setFilterType("months");
                          }}
                          className="form-check-inline"
                        />
                      </div>
                      {errors.assignType && (touched.assignType || submitCount > 0) && (
                        <div className="text-danger small mt-2">{errors.assignType}</div>
                      )}
                    </Form.Group>
                    {/* Filter Options */}
                    {values.assignType === 'filter' && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold" style={{ fontSize: "1.2rem", color: "#0A7884" }}>
                          Filter Options
                        </Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            label="Months"
                            name="filterType"
                            checked={filterType === "months"}
                            onChange={() => setFilterType("months")}
                            className="form-check-inline"
                          />
                          <Form.Check
                            type="radio"
                            label="Weeks"
                            name="filterType"
                            checked={filterType === "weeks"}
                            onChange={() => setFilterType("weeks")}
                            className="form-check-inline"
                            disabled={!selectedMonth}
                          />
                          <Form.Check
                            type="radio"
                            label="Days"
                            name="filterType"
                            checked={filterType === "days"}
                            onChange={() => setFilterType("days")}
                            className="form-check-inline"
                            disabled={!newEvent.recurringWeeks.length}
                          />
                        </div>
                        {/* Show validation summary for filter-required fields when Filter is selected */}
                        {(submitCount > 0 || touched.selectedMonth || touched.recurringWeeks || touched.recurringDays) && (
                          <div className="mt-2">
                            {errors.selectedMonth && (touched.selectedMonth || submitCount > 0) && (
                              <div className="text-danger small">{errors.selectedMonth}</div>
                            )}
                            {errors.recurringWeeks && (touched.recurringWeeks || submitCount > 0) && (
                              <div className="text-danger small">{errors.recurringWeeks}</div>
                            )}
                            {errors.recurringDays && (touched.recurringDays || submitCount > 0) && (
                              <div className="text-danger small">{errors.recurringDays}</div>
                            )}
                          </div>
                        )}
                      </Form.Group>
                    )}

                    {/* Month Selection */}
                    {filterType === "months" && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                          Select Month *
                        </Form.Label>
                        <Form.Control
                          as="select"
                          value={values.selectedMonth}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            setFieldValue('selectedMonth', selectedValue);
                            setSelectedMonth(selectedValue);
                            setFilterType("weeks"); 
                            setFieldValue('recurringWeeks', []);
                            setFieldValue('recurringDays', []);
                            setNewEvent({ ...newEvent, recurringWeeks: [], recurringDays: [] });
                          }}
                          style={{
                            border: errors.selectedMonth && values.assignType === 'filter' ? "2px solid #dc3545" : "1px solid #ced4da",
                            borderRadius: "5px",
                            padding: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <option value="" disabled>
                            -- Select Month --
                          </option>
                          {moment.months().map((month, index) => (
                            <option key={index} value={index + 1}>
                              {month}
                            </option>
                          ))}
                        </Form.Control>
                          <ErrorMessage
                          name="selectedMonth"
                          component="div"
                          className="text-danger"
                        />
                      </Form.Group>
                    )}

                    {/* Week Selection */}
                    {filterType === "weeks" && values.selectedMonth && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                          Select Week(s) *
                        </Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                          {[1, 2, 3, 4].map((week) => (
                            <Button
                              key={week}
                              style={{
                                backgroundColor: (values.recurringWeeks || []).includes(week.toString()) ? "#0A7884" : "transparent", // Custom theme color
                                color: (values.recurringWeeks || []).includes(week.toString()) ? "#fff" : "#0A7884", // Text color
                                border: errors.recurringWeeks && values.assignType === 'filter' ? "2px solid #dc3545" : "1px solid #0A7884", // Border color
                              }}
                              className="rounded-pill px-3"
                              onClick={() => {
                                const current = values.recurringWeeks || [];
                                const updatedWeeks = current.includes(week.toString())
                                  ? current.filter((w) => w !== week.toString())
                                  : [...current, week.toString()];
                                setFieldValue('recurringWeeks', updatedWeeks);
                                setNewEvent({ ...newEvent, recurringWeeks: updatedWeeks });
                              }}
                            >
                              Week {week}
                            </Button>
                          ))}
                        </div>
                        <ErrorMessage
  name="recurringWeeks"
  component="div"
  className="text-danger mt-1"
/>

                      </Form.Group>
                    )}

                    {/* Day Selection */}
                    {filterType === "weeks" && (values.recurringWeeks || []).length > 0 && (
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                          Select Days *
                        </Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                            <Button
                              key={day}
                              style={{
                                backgroundColor: (values.recurringDays || []).includes(day) ? "#0A7884" : "transparent", // Custom theme color
                                color: (values.recurringDays || []).includes(day) ? "#fff" : "#0A7884", // Text color
                                border: errors.recurringDays && values.assignType === 'filter' ? "2px solid #dc3545" : "1px solid #0A7884", // Border color
                              }}
                              className="rounded-pill px-3"
                              onClick={() => {
                                const current = values.recurringDays || [];
                                const updatedDays = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
                                setFieldValue('recurringDays', updatedDays);
                                setNewEvent({ ...newEvent, recurringDays: updatedDays });
                              }}
                            >
                              {day}
                            </Button>
                          ))}
                        </div>
                        {errors.recurringDays && (values.assignType === 'filter' || attemptedSubmit) && (
                          <div className="text-danger small mt-2 fw-semibold">{errors.recurringDays}</div>
                        )}
                      </Form.Group>
                    )}

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                            Start Time *
                          </Form.Label>
                          <Field
                            name="start"
                            as={Form.Control}
                            type="datetime-local"
                            value={values.start}
                            onChange={(e) => {
                              const newStart = e.target.value;
                              setNewEvent({ ...newEvent, start: moment(newStart).format("YYYY-MM-DD HH:mm:ss") });
                            }}
                            style={{
                              border: errors.start && touched.start ? "1px solid #dc3545" : "1px solid #ced4da",
                              borderRadius: "5px",
                              padding: "10px",
                              fontSize: "14px",
                            }}
                          />
                          {errors.start && (touched.start || submitCount > 0) && (
                            <div className="text-danger small mt-2">{errors.start}</div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-semibold" style={{ color: "var(--primary-color)" }}>
                            End Time *
                          </Form.Label>
                          <Field
                            name="end"
                            as={Form.Control}
                            type="datetime-local"
                            value={values.end}
                            onChange={(e) => {
                              const newEnd = e.target.value;
                              setNewEvent({ ...newEvent, end: moment(newEnd).format("YYYY-MM-DD HH:mm:ss") });
                            }}
                            style={{
                              border: errors.end && touched.end ? "1px solid #dc3545" : "1px solid #ced4da",
                              borderRadius: "5px",
                              padding: "10px",
                              fontSize: "14px",
                            }}
                          />
                        <ErrorMessage
  name="recurringWeeks"
  component="div"
  className="text-danger mt-1"
/>

                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </Modal.Body>
                <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={() => { setShowModal(false); setAttemptedSubmit(false); }} className="global-btn">
                    Cancel
                  </Button>
                  {newEvent.id && (
                    <Button variant="danger" onClick={() => handleDeleteEvent(newEvent.id)} className="global-btn" style={{ backgroundColor: '#dc3545' }}>
                      Delete
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="global-btn"
                    disabled={isSubmitting}
                    onClick={() => setAttemptedSubmit(true)}
                  >
                    {newEvent.id ? "Edit" : "Add"}
                  </Button>
                </Modal.Footer>
              </FormikForm>
            )}
          </Formik>
        </Modal>
      </Container>
    </div>
  );
};

export default DoctorAvailability;